import AbstractView from "abstracts/AbstractView";
import Screen from "controllers/Screen";
import Main from "controllers/Main";
import * as PIXI from 'lib/pixi.js'


export default class AgencyIntroShapes extends AbstractView {
	constructor($wrapper) {
		super();
		if (navigator.userAgent.indexOf('Speed Insights') > -1 || navigator.userAgent.indexOf('Chrome-Lighthouse') > -1) {
			return;
		}
		this.$wrapper = $wrapper;
		this.scroll = null;
		this.width = 100;
		this.height = 100;
		// this.resolution = Math_.clamp(window.devicePixelRatio, 1, 2);
		this.resolution = 1;
		this.shapes = [];
		this.isVisible = true;
		this.init();
	}

	initDOM() {
		this.$image = this.$wrapper.closest('.agency-intro').find('.agency-intro_media-wrapper');
	}

	initEl() {
		// APP
		this.app = new PIXI.Application({
			resizeTo: this.$wrapper[0],
			resolution: this.resolution,
			antialias: false,
			backgroundAlpha: 0,
			sharedTicker: false,
			sharedLoader: true
		});
		this.$wrapper.append(this.app.view);
		this.pxShape = new PIXI.Container();
		this.app.stage.addChild(this.pxShape);

		// LOAD IMAGES
		const urls = JSON.parse(this.$wrapper.attr('data-shapes-url'));
		let numLoaded = 0;
		for (let i = 0; i < urls.length; i++) {
			const shapeSpriteParent = new PIXI.Container();
			const shapeSprite = PIXI.Sprite.from(urls[i]);
			shapeSprite.texture.baseTexture.on('loaded', () => {
				numLoaded++;
				if (numLoaded === urls.length) {
					this.resize();
				}
			});
			this.pxShape.addChild(shapeSpriteParent);
			shapeSpriteParent.addChild(shapeSprite);
			shapeSprite.anchor.set(0.5);
			this.shapes.push({
				sprite: shapeSprite,
				parent: shapeSpriteParent,
			});
		}

		// DISPLACEMENT
		const displacementSprite = PIXI.Sprite.from(this.$wrapper.attr('data-displacement-url'));
		displacementSprite.texture.baseTexture.wrapMode = PIXI.WRAP_MODES.REPEAT;
		this.app.stage.addChildAt(displacementSprite, 0);
		const displacementFilter = new PIXI.filters.DisplacementFilter(displacementSprite, 50);
		displacementFilter.padding = 20;
		this.pxShape.filters = [displacementFilter];
		this.tw.displacement = gsap.to(displacementSprite, {
			duration: 20,
			ease: 'none',
			x: 1024,
			y: 512,
			repeat: -1
		});

		// SETUP POSITIONS
		this.shapes[0].start = {x: 0, y: 0.1};
		this.shapes[1].start = {x: 0, y: 0.3};
		this.shapes[2].start = {x: 0, y: 0};
		this.shapes[0].end = {x: 1.2, y: 0.7};
		this.shapes[1].end = {x: 1.2, y: 0.8};
		this.shapes[2].end = {x: 1.2, y: 1};
		this.shapes[0].duration = 4;
		this.shapes[1].duration = 3;
		this.shapes[2].duration = 5;

		// MASK
		this.pxMask = new PIXI.Graphics();
		this.app.stage.addChild(this.pxMask);

		// RESIZE TO GET THE SIZES
		this.resize();

		// THE SCROLL TWEEN
		this.tw.timeline = gsap.timeline({
			paused: true,
			onComplete: () => {
				this.tw.timeline.restart().pause();
			},
			scrollTrigger: {
				once: false,
				trigger: this.$wrapper,
				start: "center bottom",
				toggleActions: "play none none none",
				onToggle: self => {
					if (self.isActive) {
						this.resume();
					} else {
						this.pause();
					}
				}
			}
		});
		for (let i = 0; i < this.shapes.length; i++) {
			this.tw.timeline.fromTo(this.shapes[i].sprite,
				{
					x: (index, target, targets) => {
						return this.shapes[i].start.x * this.width - this.shapes[i].sprite.width / 2;
					},
					y: (index, target, targets) => {
						return this.shapes[i].start.y * this.height - this.shapes[i].sprite.height / 2;
					}
				},
				{
					x: (index, target, targets) => {
						return this.shapes[i].end.x * this.width + this.shapes[i].sprite.width / 2;
					},
					y: (index, target, targets) => {
						return this.shapes[i].end.y * this.height + this.shapes[i].sprite.height / 2;
					},
					duration: this.shapes[i].duration,
					ease: "power1.out"
				}, i / 3);
		}

		// SETS THE MASK
		this.shapes[1].parent.mask = this.pxMask;
	}

	bindEvents() {
		Screen.on(Screen.E.RESIZE, this.resize, this);
	}

	unbindEvents() {
		Screen.off(Screen.E.RESIZE, this.resize, this);
		super.unbindEvents();
	}


	resize(isMobileH) {
		if (isMobileH) {
			return;
		}
		this.width = this.$wrapper.width();
		this.height = this.$wrapper.height();
		for (let i = 0; i < this.shapes.length; i++) {
			this.shapes[i].sprite.rotation = Math.atan2((this.shapes[i].end.y * this.height + this.shapes[i].sprite.height / 2) - (this.shapes[i].start.y * this.height - this.shapes[i].sprite.height / 2), (this.shapes[i].end.x * this.width + this.shapes[i].sprite.width / 2) - (this.shapes[i].start.x * this.width - this.shapes[i].sprite.width / 2));
		}
		if (this.tw.timeline) {
			const twChildren = this.tw.timeline.getChildren();
			for (let i = 0; i < twChildren.length; i++) {
				twChildren[i].invalidate().restart();
			}
			this.tw.timeline.invalidate().restart().pause();
			this.tw.timeline.scrollTrigger.refresh();
		}


		this.updateMaskSize();
	}

	updateMaskSize() {
		// MASK 1
		this.imageBBox = this.$image[0].getBoundingClientRect();
		this.maskTop = this.$image.offset().top - this.$wrapper.offset().top;
		this.maskDiff = this.maskTop < 0 ? -this.maskTop : 0;
		this.maskTop = Math.max(0, this.maskTop);
		this.updateMask();
	}

	updateMask() {
		this.pxMask.clear();
		this.pxMask.beginFill(0xFFFFFF);
		this.pxMask.drawRect(0, 0, this.width, this.height);
		this.pxMask.endFill();
		this.pxMask.beginHole();
		this.pxMask.drawRect(this.imageBBox.x, this.maskTop, this.imageBBox.width, this.imageBBox.height - this.maskDiff);
		this.pxMask.endHole();
	}

	pause() {
		this.tw.displacement.play();
		this.app.stop();
	}

	resume() {
		this.tw.displacement.pause();
		this.app.start();
	}

	destroy() {
		super.destroy();
	}

}
